import React, { useCallback, useMemo, useRef } from 'react';
import { Form } from 'react-final-form';
import { FORM_ERROR } from 'final-form';
import {
  AutocompleteArrayInput,
  ReferenceArrayInput,
  useDataProvider,
  useGetMany,
  useNotify,
  useRefresh,
} from 'react-admin';
import makeStyles from '@material-ui/core/styles/makeStyles';
import { Theme, useTheme } from '@material-ui/core/styles';

import { Dialog } from 'components/common';
import { Input, Switch } from 'components/form';
import { composeValidators, greaterThanZero, required } from 'components/form/validationRules';
import NumberInputFieldWithActions from 'components/inputs/NumberInputFieldWithActions';

import ImageInputField from '../levels/ImageInputField';
import { RewardBadgeDto, RewardedActionDto } from '../../types';

const useStyles = makeStyles((theme: Theme) => ({
  content: {
    minWidth: theme.spacing(80),
    [theme.breakpoints.down('sm')]: {
      minWidth: 'auto',
    },
  },
  root: {
    maxWidth: '720px',
  },
  layout: {
    display: 'flex',
    gap: '32px',
    flexWrap: 'wrap',
  },
  fieldsColumn: {
    flex: 1,
    minWidth: 200,
    paddingTop: theme.spacing(1),
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  numberFields: {
    display: 'flex',
    gap: '16px',
  },
  inputNoMargin: {
    marginTop: 0,
  },
  autocompleteInput: {
    // Keep the filled variant but force a white background to match the dialog.
    '& .MuiFilledInput-root': {
      backgroundColor: theme.palette.common.white,
    },
    '& .MuiFilledInput-root:hover': {
      backgroundColor: theme.palette.common.white,
    },
    '& .MuiFilledInput-root.Mui-focused': {
      backgroundColor: theme.palette.common.white,
    },
  },
}));

interface EditBadgeDialogProps {
  open: boolean;
  onClose: () => void;
  badge?: RewardBadgeDto;
}

interface FormValues {
  icon?: File | string | null;
  title: string;
  description: string;
  actions: RewardedActionDto['id'][];
  minActionCount: string;
  points: string;
  shipmentBased?: boolean;
}

const ActionAutocompleteArrayInput = (props) => {
  const { choices = [], input } = props;
  const selectedIds = Array.isArray(input?.value) ? input.value : [];
  const { data: selectedChoices = [] } = useGetMany('rewardsAction', selectedIds);

  const mergedChoices = useMemo(() => {
    // Keep selected items in the choices list even when the server filter changes.
    const merged = new Map();
    choices.forEach((choice) => {
      if (choice && choice.id != null) {
        merged.set(choice.id, choice);
      }
    });
    selectedChoices.forEach((choice) => {
      if (choice && choice.id != null) {
        merged.set(choice.id, choice);
      }
    });
    return Array.from(merged.values());
  }, [choices, selectedChoices]);

  return <AutocompleteArrayInput {...props} choices={mergedChoices} />;
};

const EditBadgeDialog = ({ open, onClose, badge }: EditBadgeDialogProps) => {
  const classes = useStyles();
  const theme = useTheme();
  const autocompletePopperRef = useRef<any>(null);
  const dataProvider = useDataProvider();
  const refresh = useRefresh();
  const notify = useNotify();

  const initialValues: FormValues = useMemo(() => {
    // Assumption: badge actions include IDs; missing IDs will skip preselecting actions.
    const actionIds = Array.isArray(badge?.actions)
      ? badge.actions
          .map((action) => (typeof action === 'string' ? action : action?.id))
          .filter((id): id is string => Boolean(id))
      : [];

    return {
      icon: badge?.iconUrl ?? '',
      title: badge?.title ?? '',
      description: badge?.description ?? '',
      actions: actionIds,
      minActionCount:
        typeof badge?.minActionCount === 'number' ? badge.minActionCount.toString() : '0',
      // Assume the API accepts 0 when points are omitted; adjust if points become required.
      points: typeof badge?.points === 'number' ? badge.points.toString() : '0',
      shipmentBased: badge?.shipmentBased ?? false,
    };
  }, [badge]);

  const handleSubmitForm = async (values: FormValues) => {
    try {
      const formData = new FormData();

      formData.append('title', values.title?.trim() ?? '');
      formData.append('description', values.description?.trim() ?? '');
      formData.append('minActionCount', values.minActionCount ?? '0');
      formData.append('points', values.points ?? '0');
      // Assumption: the API accepts boolean values in FormData as 'true'/'false' strings.
      formData.append('shipmentBased', values.shipmentBased ? 'true' : 'false');

      if (values.actions?.length) {
        values.actions.forEach((id) => {
          if (id) {
            formData.append('actions', id);
          }
        });
      } else {
        formData.append('actions', '');
      }

      if (values.icon instanceof File) {
        formData.append('icon', values.icon);
      } else if (typeof values.icon === 'string' && values.icon) {
        formData.append('icon', values.icon);
      }

      const response = badge?.id
        ? await dataProvider.update('rewardsBadge', {
            id: badge.id,
            data: formData,
          })
        : await dataProvider.post('rewardsBadge', {
            data: formData,
          });

      refresh();
      onClose();

      return response;
    } catch (error: any) {
      const message = error?.message || 'Failed to save rewards badge';
      let errorPayload;

      if (typeof error?.message === 'string') {
        try {
          errorPayload = JSON.parse(error.message);
        } catch (parseError) {
          // No-op: fall back to other payload locations.
        }
      }

      if (!errorPayload && error?.body) {
        errorPayload = error.body;
      }

      if (!errorPayload && error?.response?.body) {
        errorPayload = error.response.body;
      }

      const fieldErrors = Array.isArray(errorPayload?.errors)
        ? errorPayload.errors.reduce(
            (acc, err) => {
              if (err?.field) {
                const errorText = err?.description ?? 'Invalid value';
                acc[err.field] = acc[err.field] ? `${acc[err.field]}, ${errorText}` : errorText;
              }
              return acc;
            },
            {} as Record<string, string>
          )
        : {};

      notify(message, 'error');

      if (Object.keys(fieldErrors).length) {
        return { [FORM_ERROR]: message, ...fieldErrors };
      }

      return { [FORM_ERROR]: message };
    }
  };

  const handleAutocompleteStateChange = useCallback(() => {
    // Keep the suggestions popper aligned when input height/position changes.
    autocompletePopperRef.current?.scheduleUpdate?.();
  }, []);

  if (!open) {
    return null;
  }

  return (
    <Form<FormValues>
      key={badge?.id ?? 'new-badge'}
      initialValues={initialValues}
      onSubmit={handleSubmitForm}
      render={({ handleSubmit, submitting, submitError, values }) => {
        // Assumption: "total actions" equals the number of selected actions in the badge.
        const actionsCount = Array.isArray(values?.actions) ? values.actions.length : 0;
        // Shipment-based badges skip the max/adornment; validation should handle any upper bounds.
        const isShipmentBased = Boolean(values?.shipmentBased);

        return (
          <form onSubmit={handleSubmit}>
            <Dialog
              open={open}
              onCancel={onClose}
              title={badge?.id ? 'Edit badge' : 'Add badge'}
              onConfirm={handleSubmit}
              confirmText="Save"
              submitting={submitting}
              error={submitError}
              classes={{ content: classes.content, root: classes.root }}>
              <div className={classes.layout}>
                <ImageInputField name="icon" />
                <div className={classes.fieldsColumn}>
                  <Input
                    name="title"
                    label="Badge name"
                    validate={required}
                    classes={{ input: classes.inputNoMargin }}
                  />
                  <Input
                    name="description"
                    label="Description"
                    validate={required}
                    inputProps={{ multiline: true, rows: 4 }}
                  />
                  <Switch name="shipmentBased" label="Shipment based" />
                  {/* Actions are optional for shipment-based badges. */}
                  {!isShipmentBased && (
                    <ReferenceArrayInput
                      label="Actions"
                      source="actions"
                      reference="rewardsAction"
                      resource="rewardsBadge"
                      perPage={100}
                      sort={{ field: 'id', order: 'ASC' }}
                      filterToQuery={(searchText) => ({ search: searchText })}
                      validate={required}>
                      <ActionAutocompleteArrayInput
                        optionText="title"
                        optionValue="id"
                        fullWidth
                        variant="filled"
                        onStateChange={handleAutocompleteStateChange}
                        options={{
                          className: classes.autocompleteInput,
                          suggestionsContainerProps: {
                            placement: 'bottom-start',
                            popperRef: autocompletePopperRef,
                            style: { zIndex: theme.zIndex.modal + 1 },
                          },
                        }}
                      />
                    </ReferenceArrayInput>
                  )}
                  <div className={classes.numberFields}>
                    <NumberInputFieldWithActions
                      name="minActionCount"
                      label="Min Actions Required"
                      validate={composeValidators(required, greaterThanZero)}
                      isInteger
                      actionButtons
                      endAdornmentText={isShipmentBased ? undefined : `/${actionsCount}`}
                      maxValue={isShipmentBased ? undefined : actionsCount}
                      clampValue
                    />
                    <NumberInputFieldWithActions
                      name="points"
                      label="Points"
                      validate={composeValidators(required, greaterThanZero)}
                      isInteger
                      actionButtons
                      startAdornment={null}
                    />
                  </div>
                </div>
              </div>
            </Dialog>
          </form>
        );
      }}
    />
  );
};

export default EditBadgeDialog;
